import React, { useState } from 'react';
import {
  IcTrophy,
  IcFilters,
  IcChevronDown,
  IcChevronLeft,
  IcChevronRight,
  IcHistory,
  IcCalendar,
  IcCalendarRange,
} from '../../theme/icons';

// Shared building blocks for the mobile statistics pages (ventes + générale).
// Les cartes KPI viennent maintenant du StatCard desktop (statsUi) pour que
// mobile et PC affichent le même détail et les mêmes couleurs (Ahmed
// 2026-08-17) ; ici ne restent que le filtre de période et les enveloppes.

export const PERIODS = [
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
  { key: 'month', label: 'Ce mois' },
  { key: 'year', label: 'Cette année' },
];

export const fmt = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// ---------------------------------------------------------------------------
// Filtre de période dans UN SEUL bouton déroulant — même design que le bouton
// filtre de la page historique ventes (chip VenteTabs mobile : blanc, bordure
// noire, chevron). Le menu liste les 5 périodes rapides + « Choisir un mois »
// et « Choisir une semaine » via un mini-calendrier. `period` est soit une clé
// de PERIODS, soit { start, end, label } (dates inclusives YYYY-MM-DD).
// `accentClass` colore la sélection dans les calendriers (accent de la page).
// `align="right"` colle le chip au bord droit (comme le bouton Filtres de la
// page nouvelle vente) et ancre le menu à droite pour qu'il reste à l'écran.
// ---------------------------------------------------------------------------

const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const MONTHS_LONG = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const pad2 = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// Lundi de la semaine contenant `d`.
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

// inline : pas de marge basse — pour poser le chip sur la même ligne que le
// titre de page (Ahmed 2026-08-16).
// Sélection dans les calendriers = aplat encre (l'aplat rouge #DC2626 est
// réservé à l'impayé / rupture).
export function PeriodFilterButton({ period, setPeriod, accentClass = 'bg-gray-900 text-white', align = 'left', inline = false }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | month | week
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const isCustom = typeof period === 'object' && period !== null;
  const label = isCustom
    ? period.label
    : (PERIODS.find((p) => p.key === period)?.label || period);

  const close = () => { setOpen(false); setView('list'); };

  const pickPreset = (key) => { setPeriod(key); close(); };

  const pickMonth = (y, m) => {
    setPeriod({
      start: isoDate(new Date(y, m, 1)),
      end: isoDate(new Date(y, m + 1, 0)),
      label: `${MONTHS_LONG[m]} ${y}`,
    });
    close();
  };

  const pickWeek = (d) => {
    const s = mondayOf(d);
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (x) => x.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    setPeriod({ start: isoDate(s), end: isoDate(e), label: `${f(s)} – ${f(e)}` });
    close();
  };

  // Cellules du calendrier (vue semaine) : mois affiché, lundi en premier.
  const firstOfCal = new Date(calYear, calMonth, 1);
  const leadingBlanks = (firstOfCal.getDay() + 6) % 7;
  const daysInCal = new Date(calYear, calMonth + 1, 0).getDate();
  const calAtCurrent = calYear === today.getFullYear() && calMonth === today.getMonth();
  const shiftCalMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear()); setCalMonth(d.getMonth());
  };

  const itemCls = (active) =>
    `w-full text-left flex items-center gap-2.5 px-4 py-3 text-sm font-semibold border-b border-gray-50 last:border-b-0 ${
      active ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
    }`;
  // Pastilles de navigation (mois / année) = famille btn-day.
  const navBtnCls = 'btn-day h-8 w-8';

  return (
    <div className={`relative ${inline ? '' : 'mb-4'} ${align === 'right' ? 'flex justify-end' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-white border-gray-900 text-gray-900 shadow-sm transition-colors"
      >
        <IcFilters size={13} /> {label}
        <IcChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Clic hors du menu = fermer */}
          <button type="button" aria-label="Fermer le filtre" onClick={close} className="fixed inset-0 z-30 cursor-default" />
          <div className={`absolute z-40 top-full mt-1 ${align === 'right' ? 'right-0' : 'left-0'} bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden ${view === 'list' ? 'min-w-[13rem]' : 'w-72'}`}>

            {view === 'list' && (
              <>
                {PERIODS.map((pd) => (
                  <button key={pd.key} type="button" onClick={() => pickPreset(pd.key)} className={itemCls(period === pd.key)}>
                    <IcHistory size={13} /> {pd.label}
                  </button>
                ))}
                <button type="button" onClick={() => setView('month')} className={itemCls(false)}>
                  <IcCalendar size={13} /> Choisir un mois…
                </button>
                <button type="button" onClick={() => setView('week')} className={itemCls(false)}>
                  <IcCalendarRange size={13} /> Choisir une semaine…
                </button>
              </>
            )}

            {view === 'month' && (
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <button type="button" onClick={() => setView('list')} aria-label="Retour" className={navBtnCls}>
                    <IcChevronLeft size={11} />
                  </button>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setCalYear((y) => y - 1)} aria-label="Année précédente" className={navBtnCls}>
                      <IcChevronLeft size={11} />
                    </button>
                    <span className="text-sm font-bold text-gray-900">{calYear}</span>
                    <button
                      type="button" onClick={() => setCalYear((y) => y + 1)} aria-label="Année suivante"
                      disabled={calYear >= today.getFullYear()} className={navBtnCls}
                    >
                      <IcChevronRight size={11} />
                    </button>
                  </div>
                  <span className="w-8" />
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {MONTHS_SHORT.map((m, i) => {
                    const future = calYear > today.getFullYear() ||
                      (calYear === today.getFullYear() && i > today.getMonth());
                    const selected = isCustom && period.start === isoDate(new Date(calYear, i, 1)) &&
                      period.end === isoDate(new Date(calYear, i + 1, 0));
                    return (
                      <button
                        key={m} type="button" disabled={future} onClick={() => pickMonth(calYear, i)}
                        className={`h-10 rounded-lg text-sm font-semibold transition-colors ${
                          selected ? accentClass
                            : future ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {view === 'week' && (
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <button type="button" onClick={() => setView('list')} aria-label="Retour" className={navBtnCls}>
                    <IcChevronLeft size={11} />
                  </button>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => shiftCalMonth(-1)} aria-label="Mois précédent" className={navBtnCls}>
                      <IcChevronLeft size={11} />
                    </button>
                    <span className="text-sm font-bold text-gray-900 w-24 text-center">{MONTHS_SHORT[calMonth]} {calYear}</span>
                    <button
                      type="button" onClick={() => shiftCalMonth(1)} aria-label="Mois suivant"
                      disabled={calAtCurrent} className={navBtnCls}
                    >
                      <IcChevronRight size={11} />
                    </button>
                  </div>
                  <span className="w-8" />
                </div>
                <div className="grid grid-cols-7 gap-0.5 mb-1 text-center text-[11px] font-semibold text-gray-400">
                  {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <span key={i}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {Array.from({ length: leadingBlanks }).map((_, i) => <span key={`b${i}`} />)}
                  {Array.from({ length: daysInCal }).map((_, i) => {
                    const d = new Date(calYear, calMonth, i + 1);
                    const future = d > today;
                    const inSelectedWeek = isCustom && period.start === isoDate(mondayOf(d)) &&
                      period.label && period.label.includes('–');
                    return (
                      <button
                        key={i} type="button" disabled={future} onClick={() => pickWeek(d)}
                        className={`h-9 rounded-lg text-sm font-medium transition-colors ${
                          inSelectedWeek ? accentClass
                            : future ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] text-gray-400 text-center">Touchez un jour : sa semaine (lun → dim) est sélectionnée.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// "Meilleur jour" highlight card — carte sombre du livre (Brand Kit §06 mock,
// même anatomie que sur le Dashboard) : #1F2937 rayon 10 padding 10 bordée
// amber #D97706, halo amber-500/18, trophée 17 px amber-500, libellé 10.5 px
// amber-300, « montant · date » 700 #F0FDF4 tabulaire + date 11 px 400
// gray-400. `mk-dark` = remap clair (mobile-theme.css).
export function GoldenBestDay({ bestDay }) {
  if (!bestDay) return null;
  return (
    <div className="mk-dark rounded-2xl p-4 bg-gray-800 border border-amber-600 shadow-[0_0_12px_theme(colors.amber.500/18%)] flex items-center gap-2">
      <IcTrophy size={12} className="text-amber-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-amber-300 mb-2">Meilleur jour · sur la période</div>
        <div className="font-bold text-lg text-green-50 truncate tabular-nums" title={fmt(bestDay.ca)}>
          {fmt(bestDay.ca)}
          <span className="font-normal text-gray-400 text-base"> · {fmtDate(bestDay.date)}</span>
        </div>
      </div>
    </div>
  );
}

// White section card wrapper (chart / top products / categories / history).
export function WhiteCard({ title, icon: Icon, iconClass, right, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`${iconClass} shrink-0`} />} <span className="truncate">{title}</span>
        </h2>
        {right}
      </div>
      {children}
    </div>
  );
}

// Horizontal category bars (revenue/montant). `items` = [{name, <valueKey>}].
export function CategoryBars({ items, valueKey }) {
  const max = Math.max(1, ...(items || []).map((c) => c[valueKey] || 0));
  if (!items || items.length === 0) return <p className="text-gray-400 text-sm text-center py-8">Aucune donnée.</p>;
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-gray-700 truncate">{c.name}</span>
            <span className="text-gray-500">{fmt(c[valueKey])}</span>
          </div>
          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.min(100, ((c[valueKey] || 0) / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
